import { formatDateToLocale } from "@/common/helpers/format-date";
import { DeviceType } from "@/common/hooks/use-device";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { ImplementedAccessorColumnDef } from "@/components/data-table/types";
import { EmptyValue } from "@/components/ui/empty-value";
import i18next from "i18next";

export interface HasName {
  name?: string | null;
}
export interface HasTechnicalId {
  technicalId?: string | null;
}
export interface HasDeletedAt {
  deletedAt?: string | null;
}
export interface HasV1Id {
  v1Id?: string | number | null;
}
export interface HasDatId {
  datId?: string | null;
}
export interface HasDistance {
  distance?: number | null;
}

export const commonColumns = {
  columnUuid: <T extends RowWithIdentifier>() =>
    ({
      id: "uuid",
      size: 150,
      enableSorting: true,
      header: () => i18next.t("common:UUID"),
      visibility: [DeviceType.Widescreen],
      accessorFn: (row: T) => row.uuid,
      cell: (cell) => {
        return automaticCellRenderer(cell.getValue());
      },
    }) as ImplementedAccessorColumnDef<T, string>,

  columnDistance: <T extends RowWithIdentifier & HasDistance>() =>
    ({
      id: "distance",
      size: 120,
      enableSorting: true,
      header: () => i18next.t("common:Distance"),
      visibility: [DeviceType.Desktop, DeviceType.Widescreen],
      accessorFn: (row: T) => row.distance,
      cell: (row) => {
        const distance = row.getValue() as number | undefined;
        if (typeof distance === "number") {
          return automaticCellRenderer(`${distance.toFixed(1)} ${i18next.t("common:km")}`);
        }

        return <EmptyValue />;
      },
    }) as ImplementedAccessorColumnDef<T, number | undefined>,

  columnV1Id: <T extends RowWithIdentifier & HasV1Id>() =>
    ({
      id: "v1Id",
      size: 50,
      enableSorting: true,
      align: "right",
      header: () => i18next.t("masterdata:V1ID"),
      visibility: [], // Hidden by default
      accessorFn: (row: T) => row.v1Id,
      cell: (row) => {
        const v1Id = row.getValue();
        if (!v1Id) {
          return <EmptyValue />;
        }
        return automaticCellRenderer(`${v1Id}`);
      },
    }) as ImplementedAccessorColumnDef<T, string | number | null | undefined>,

  columnDatId: <T extends RowWithIdentifier>(
    accessorFn: (row: T) => string | null | undefined = (row) => (row as T & HasDatId).datId,
    enableSorting = true,
  ) =>
    ({
      id: "datId",
      size: 80,
      enableSorting,
      header: () => i18next.t("masterdata:DATID"),
      visibility: [DeviceType.Desktop, DeviceType.Widescreen],
      accessorFn,
      cell: (row) => {
        const datId = row.getValue();
        if (!datId) {
          return <EmptyValue />;
        }

        return automaticCellRenderer(datId);
      },
    }) as ImplementedAccessorColumnDef<T, string | null | undefined>,

  columnCreatedAt: <T extends RowWithIdentifier>() =>
    ({
      id: "createdAt",
      size: 150,
      enableSorting: true,
      header: () => i18next.t("common:CreatedAt"),
      align: "right",
      visibility: [DeviceType.Desktop, DeviceType.Widescreen],
      accessorFn: (row: T) => row.createdAt,
      cell: (info) => {
        const date = info.getValue();
        if (!date) {
          return <EmptyValue />;
        }
        return formatDateToLocale(date);
      },
    }) as ImplementedAccessorColumnDef<T, string | undefined>,

  columnUpdatedAt: <T extends RowWithIdentifier>() =>
    ({
      id: "updatedAt",
      size: 150,
      enableSorting: true,
      header: () => i18next.t("common:UpdatedAt"),
      align: "right",
      visibility: [DeviceType.Desktop, DeviceType.Widescreen],
      accessorFn: (row: T) => row.updatedAt,
      cell: (info) => {
        const date = info.getValue();
        if (!date) {
          return <EmptyValue />;
        }
        return formatDateToLocale(date);
      },
    }) as ImplementedAccessorColumnDef<T, string | undefined>,

  columnDeletedAt: <T extends RowWithIdentifier & HasDeletedAt>() =>
    ({
      id: "deletedAt",
      size: 150,
      enableSorting: true,
      header: () => i18next.t("common:Deleted"),
      align: "right",
      visibility: [DeviceType.Desktop, DeviceType.Widescreen],
      accessorFn: (row: T) => row.deletedAt,
      cell: (info) => {
        const date = info.getValue();
        if (!date) {
          return <EmptyValue />;
        }
        return formatDateToLocale(date);
      },
    }) as ImplementedAccessorColumnDef<T, string | undefined>,

  columnName: <T extends RowWithIdentifier & HasName>() =>
    ({
      id: "name",
      size: 200,
      enableSorting: true,
      header: () => i18next.t("common:Name"),
      accessorFn: (row: T) => row.name,
      cell: (row) => automaticCellRenderer(row.getValue(), { convertString: false }),
    }) as ImplementedAccessorColumnDef<T, string | undefined>,

  columnTechnicalId: <T extends RowWithIdentifier & HasTechnicalId>() =>
    ({
      id: "technicalId",
      size: 150,
      enableSorting: true,
      header: () => i18next.t("common:TechnicalId"),
      visibility: [DeviceType.Desktop, DeviceType.Widescreen],
      accessorFn: (row: T) => row.technicalId,
      cell: (row) => automaticCellRenderer(row.getValue()),
    }) as ImplementedAccessorColumnDef<T, string | undefined>,
};
